// src/store/slices/profileSlice.ts
// 👤 Социальные действия и промпты

import type { StateCreator } from 'zustand';

// ========== HELPER ==========

const daysBetween = (date1: string, date2: string): number => {
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  const diffTime = Math.abs(d2.getTime() - d1.getTime());
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

// ========== SLICE STATE ==========

export interface ProfileSlice {
  // State
  shareCount: number;
  referralCount: number;
  feedbackCount: number;
  lastFeedbackPrompt: string | null;
  upgradePromptShown: boolean;
  upgradeRemindDate: string | null;

  // Actions
  incrementShareCount: () => void;
  incrementReferralCount: () => void;
  incrementFeedbackCount: () => void;
  shouldShowFeedbackPrompt: () => boolean;
  markFeedbackPromptShown: () => void;
  shouldShowUpgradePrompt: () => boolean;
  markUpgradePromptShown: (remind: boolean) => void;
  resetProfile: () => void;
}

// ========== INITIAL STATE ==========

const initialProfileState = {
  shareCount: 0,
  referralCount: 0,
  feedbackCount: 0,
  lastFeedbackPrompt: null,
  upgradePromptShown: false,
  upgradeRemindDate: null,
};

// ========== SLICE CREATOR ==========

export const createProfileSlice: StateCreator<
  ProfileSlice,
[],
[],
  ProfileSlice
> = (set, get) => ({
  ...initialProfileState,

  incrementShareCount: () => {
    set((state) => ({ shareCount: state.shareCount + 1 }));
    if (process.env.NODE_ENV === 'development') {
      console.log('📤 Share count увеличен');
    }
  },

  incrementReferralCount: () => {
    set((state) => ({ referralCount: state.referralCount + 1 }));
    if (process.env.NODE_ENV === 'development') {
      console.log('🎁 Referral count увеличен');
    }
  },

  incrementFeedbackCount: () => {
    set((state) => ({ feedbackCount: state.feedbackCount + 1 }));
    if (process.env.NODE_ENV === 'development') {
      console.log('💬 Feedback count увеличен');
    }
  },

  shouldShowFeedbackPrompt: () => {
    const state = get();
    const fullState = get() as any;
    const applications = fullState.applications ||[];
    const now = new Date().toISOString();

    if (state.lastFeedbackPrompt) {
      const daysSince = daysBetween(state.lastFeedbackPrompt, now);
      if (daysSince < 7) return false;
    }

    const recentApps = applications.filter((a: any) => {
      if (!state.lastFeedbackPrompt) return true;
      return a.date > state.lastFeedbackPrompt;
    });

    return recentApps.length >= 3;
  },

  markFeedbackPromptShown: () => {
    set({ lastFeedbackPrompt: new Date().toISOString() });
    if (process.env.NODE_ENV === 'development') {
      console.log('✅ Feedback prompt отмечен');
    }
  },

  shouldShowUpgradePrompt: () => {
    const state = get();
    const fullState = get() as any;
    const startDate = fullState.startDate;
    const currentStreak = fullState.currentStreak || 0;

    if (!startDate || state.upgradePromptShown) return false;

    if (state.upgradeRemindDate) {
      const now = new Date().toISOString();
      if (now < state.upgradeRemindDate) return false;
    }

    return currentStreak >= 84;
  },

  markUpgradePromptShown: (remind) => {
    if (remind) {
      const remindDate = new Date();
      remindDate.setDate(remindDate.getDate() + 14);
      set({ upgradeRemindDate: remindDate.toISOString() });
      if (process.env.NODE_ENV === 'development') {
        console.log('⏰ Upgrade prompt отложен на 14 дней');
      }
    } else {
      set({ upgradePromptShown: true });
      if (process.env.NODE_ENV === 'development') {
        console.log('✅ Upgrade prompt скрыт навсегда');
      }
    }
  },

  resetProfile: () => {
    set(initialProfileState);
  },
});